/**
 * skaile library — manage user-facing libraries (authoring places for AI
 * assets, backed by `local` or `git`).
 *
 * Lifecycle subcommands: list, status, show, init, link, create, default,
 * rename, set, remove. Git verbs (commit, pull, push, propose, git, sync)
 * apply to git-backed libraries. Publish/register/yank (in `library-publish.ts`)
 * push + register the library repo and publish its manifest to the store.
 *
 * @docLink cli/commands/library
 */

import * as fs from "node:fs";
import { existsSync } from "node:fs";
import * as path from "node:path";
import * as p from "@clack/prompts";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import {
  ASSET_KINDS,
  loadSkPublishManifest,
  parseFrontmatter,
  saveSkPublishManifest,
  scanDirectory,
  slugifyAssetName,
} from "@skaile/workspaces/core";
import { resolveLibraryDir } from "@skaile/workspaces/library";
import { Command, Option } from "commander";
import pc from "picocolors";
import { stringify as stringifyYaml } from "yaml";
import { formatRelativeTime, logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import { openLibrary, openLibraryManager } from "../open-library.ts";
import { createFullRegistry } from "../open-registry.ts";
import { getStoreIdentity } from "../store-auth.ts";
import { indexLibrary, resolveLibraryPublisher } from "./library-index.ts";
import { ensureDomain, listDomains, upsertManifestAsset } from "./skailify-lib.ts";

export function makeLibraryCommand(): Command {
  const cmd = new Command("library").description("Manage user authoring libraries (local / git)");

  // ── library list ─────────────────────────────────────────────────────────
  cmd
    .command("list")
    .description("List all registered libraries")
    .option("--json", "Output as JSON")
    .action(async (opts: { json?: boolean }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const ls = await manager.listLibraries();
        if (opts.json) return console.log(JSON.stringify(ls, null, 2));
        if (ls.length === 0) {
          logInfo("No libraries registered. Run `skaile library init personal` to start.");
          return;
        }
        console.log();
        console.log(S.heading("  Libraries"));
        console.log(`  ${S.rule(80)}`);
        for (const l of ls) {
          const def = l.isDefault ? pc.green("▸ ") : "  ";
          console.log(
            `${def}${S.cmd(l.name.padEnd(16))}  ${pc.dim(l.backend.padEnd(7))}  ${pc.dim(l.ownership.padEnd(12))}  ${pc.dim(l.path)}`,
          );
        }
        console.log(`\n  ${S.dim(`${ls.length} library(s)`)}\n`);
      } finally {
        close();
      }
    });

  // ── library status ───────────────────────────────────────────────────────
  // Folds in the former top-level `lib-status`. With no <name>, prints a
  // health summary of the local Library; with a <name>, prints sync status
  // for that single library only.
  cmd
    .command("status [name]")
    .description("Show overall Library health or per-library sync status")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--json", "Output as JSON")
    .action(async (name: string | undefined, opts: { projectDir: string; json?: boolean }) => {
      if (name) {
        await printPerLibraryStatus(name, opts.json ?? false);
        return;
      }
      await printOverallStatus(opts.projectDir, opts.json ?? false);
    });

  // ── library show ─────────────────────────────────────────────────────────
  cmd
    .command("show <name>")
    .description("Detailed info about a library")
    .action(async (name: string) => {
      const { manager, close } = await openLibraryManager();
      try {
        const l = await manager.requireLibrary(name);
        console.log();
        console.log(S.heading(`  ${l.name}`));
        console.log(`  ${S.rule(50)}`);
        console.log(`  ${S.label("id")}            ${l.id}`);
        console.log(`  ${S.label("backend")}       ${l.backend}`);
        console.log(`  ${S.label("ownership")}     ${l.ownership}`);
        console.log(`  ${S.label("path")}          ${l.path}`);
        console.log(`  ${S.label("structure")}     ${l.structure ?? "(undetected)"}`);
        console.log(`  ${S.label("isDefault")}     ${l.isDefault}`);
        console.log(`  ${S.label("manifestGen")}   ${l.manifestGenerated}`);
        console.log(`  ${S.label("backendConfig")} ${JSON.stringify(l.backendConfig)}`);
        console.log();
      } finally {
        close();
      }
    });

  // ── library init ─────────────────────────────────────────────────────────
  // Default backend is `local`. --git creates with a remote attached.
  cmd
    .command("init <name>")
    .description("Create a new local library at ~/.skaile/libraries/<name>")
    .option("--git <url>", "Initialize as a git library tracking <url>")
    .option("--owner", "Mark as owner (default for local)")
    .option("--contributor", "Mark as contributor")
    .option("--reader", "Mark as reader")
    .action(
      async (
        name: string,
        opts: {
          git?: string;
          owner?: boolean;
          contributor?: boolean;
          reader?: boolean;
        },
      ) => {
        const libDir = resolveLibraryDir();
        const libPath = path.join(libDir, name);
        const ownership = opts.contributor ? "contributor" : opts.reader ? "reader" : "owner";
        const { manager, close } = await openLibraryManager();
        try {
          if (opts.git) {
            fs.mkdirSync(libPath, { recursive: true });
            await manager.addLibrary({
              name,
              path: libPath,
              backend: "git",
              backendConfig: { url: opts.git, branch: "main", authHint: "ssh" },
              ownership,
            });
          } else {
            fs.mkdirSync(libPath, { recursive: true });
            await manager.addLibrary({
              name,
              path: libPath,
              backend: "local",
              ownership: "owner",
              structure: "domain",
            });
          }
          logOk(`Library "${name}" created at ${libPath}`);
        } finally {
          close();
        }
      },
    );

  // ── library link ─────────────────────────────────────────────────────────
  // Attach a github remote to a local library, making it store-publishable.
  cmd
    .command("link <name> <git-url>")
    .description("Attach a github remote to a local library (makes it store-publishable)")
    .option("--branch <name>", "Default branch", "main")
    .action(async (name: string, url: string, opts: { branch: string }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        if (lib.backend === "git") {
          logErr(
            `Library "${name}" is already git-backed. ` +
              `Use \`skaile library set ${name} backendConfig.url=${url}\` to change its remote.`,
          );
          process.exit(1);
        }
        await manager.updateLibrary(name, {
          backend: "git",
          backendConfig: { url, branch: opts.branch, authHint: "ssh" },
        });
        logOk(`Library "${name}" linked to ${url}.`);
      } finally {
        close();
      }
    });

  // ── library create ───────────────────────────────────────────────────────
  // Scaffold a new asset inside a library. Mirrors the top-level `skaile
  // create` but rooted at the library checkout dir by default.
  cmd
    .command("create <kind> <name>")
    .description("Scaffold a new asset inside the default (or named) library")
    .addOption(new Option("--kind <kind>", "Asset kind override").choices([...ASSET_KINDS]))
    .option("--library <name>", "Target library (default: the default-marked one)")
    .option("--dir <dir>", "Target directory inside the library (default: library root)")
    .option("--publisher <name>", "Publisher for the new asset (default: the library's default)")
    .action(
      async (
        kind: string,
        name: string,
        opts: { kind?: string; library?: string; dir?: string; publisher?: string },
      ) => {
        if (!(ASSET_KINDS as readonly string[]).includes(kind)) {
          logErr(`Unknown asset kind "${kind}". Expected: ${ASSET_KINDS.join(", ")}.`);
          process.exit(1);
        }
        const { manager, library, close } = await openLibraryManager();
        try {
          let target: Awaited<ReturnType<typeof manager.requireLibrary>>;
          if (opts.library) {
            target = await manager.requireLibrary(opts.library);
          } else {
            const all = await manager.listLibraries();
            const def = all.find((l) => l.isDefault);
            if (!def) {
              logErr(
                "No default library set. Either run `skaile library default <name>` first or pass --library.",
              );
              process.exit(1);
            }
            target = def;
          }
          const effectiveKind = opts.kind ?? kind;
          const am = new AssetManager({ projectDir: process.cwd() });
          const destDir = opts.dir ? path.join(target.path, opts.dir) : target.path;
          const res = am.create(name, effectiveKind, destDir);
          if (!res.ok) {
            logErr(res.path);
            process.exit(1);
          }
          logOk(`Created ${effectiveKind}: ${pc.dim(res.path)}`);

          // Resolve the publisher and make the asset immediately consumable:
          // stamp the resolved publisher (frontmatter + library manifest, the
          // latter is what discovery keys on), then index into asset_definitions.
          const domain = opts.dir ? opts.dir.split(path.sep)[0] : undefined;
          const publisher = resolveLibraryPublisher(target, opts, domain, getStoreIdentity());
          stampAssetPublisher(res.path, publisher);
          ensureManifestPublisher(target.path, publisher);
          // Register the new asset in the manifest `assets[]` so `library publish`
          // (which reads that inventory, not discovery) actually ships it. `root`
          // is the asset's repo-relative dir; the scaffold stamps version 0.1.0.
          upsertManifestAsset(target.path, {
            kind: effectiveKind,
            name,
            root: path.relative(target.path, res.path) || name,
            version: "0.1.0",
          });
          const idx = await indexLibrary(target, library);
          for (const e of idx.errors) logWarn(e);
          logOk(`Indexed — add it with \`skaile add ${effectiveKind}:@${publisher}/${name}\``);
        } finally {
          close();
        }
      },
    );

  // ── library domain ─────────────────────────────────────────────────────────
  // Domains are the first-level directories of a library and define the
  // publisher namespace. Before this command they were born implicitly via
  // `mkdir`; now `create` scaffolds a DOMAIN.md and a publisher slug.
  const domain = cmd
    .command("domain")
    .description("Manage domains (publisher namespaces) inside a library");

  /** Resolve the target library: explicit --library, else the writable default. */
  async function resolveTargetLibrary(
    manager: Awaited<ReturnType<typeof openLibraryManager>>["manager"],
    libraryName: string | undefined,
  ): Promise<Awaited<ReturnType<typeof manager.requireLibrary>>> {
    if (libraryName) return manager.requireLibrary(libraryName);
    const all = await manager.listLibraries();
    const def = all.find((l) => l.isDefault);
    if (def) return def;
    // No explicit default, but a single library is unambiguous — use it.
    if (all.length === 1) return all[0];
    if (all.length === 0) {
      logErr("No libraries registered. Run `skaile library init <name>` first.");
      process.exit(1);
    }
    logErr("No default library set. Run `skaile library default <name>` or pass --library.");
    process.exit(1);
  }

  domain
    .command("create <name>")
    .description("Scaffold a domain dir + DOMAIN.md inside a library")
    .option("--library <name>", "Target library (default: the default-marked one)")
    .option("--publisher <p>", "Publisher slug (default: the domain name kebab-cased)")
    .option("--description <text>", "Domain description for DOMAIN.md")
    .action(
      async (
        name: string,
        opts: { library?: string; publisher?: string; description?: string },
      ) => {
        const { manager, close } = await openLibraryManager();
        try {
          const target = await resolveTargetLibrary(manager, opts.library);
          if (target.ownership === "reader") {
            logErr(`Library "${target.name}" is read-only (ownership=reader).`);
            process.exit(1);
          }
          const res = ensureDomain(target.path, name, {
            publisher: opts.publisher,
            description: opts.description,
          });
          // A domain-structured library is the natural home for domains.
          if (target.structure !== "domain") {
            await manager.updateLibrary(target.id, { structure: "domain" });
          }
          if (res.created) {
            logOk(`Created domain ${S.cmd(name)} (publisher @${res.publisher}) → ${res.dir}`);
          } else {
            logInfo(`Domain ${S.cmd(name)} already exists at ${res.dir}`);
          }
        } finally {
          close();
        }
      },
    );

  domain
    .command("list")
    .description("List domains in a library")
    .option("--library <name>", "Target library (default: the default-marked one)")
    .option("--json", "Output as JSON")
    .action(async (opts: { library?: string; json?: boolean }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const target = await resolveTargetLibrary(manager, opts.library);
        const domains = listDomains(target.path);
        if (opts.json) return console.log(JSON.stringify(domains, null, 2));
        if (domains.length === 0) {
          logInfo(
            `No domains in "${target.name}". Create one: \`skaile library domain create <name>\`.`,
          );
          return;
        }
        for (const d of domains) console.log(`  ${S.cmd(d)}`);
        console.log(`\n  ${S.dim(`${domains.length} domain(s) in ${target.name}`)}\n`);
      } finally {
        close();
      }
    });

  domain
    .command("show <name>")
    .description("Print a domain's DOMAIN.md")
    .option("--library <name>", "Target library (default: the default-marked one)")
    .action(async (name: string, opts: { library?: string }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const target = await resolveTargetLibrary(manager, opts.library);
        const domainFile = path.join(target.path, name, "DOMAIN.md");
        if (!existsSync(domainFile)) {
          logErr(`Domain "${name}" not found in "${target.name}" (no ${domainFile}).`);
          process.exit(1);
        }
        console.log(fs.readFileSync(domainFile, "utf8"));
      } finally {
        close();
      }
    });

  // ── library default ──────────────────────────────────────────────────────
  cmd
    .command("default <name>")
    .description("Set <name> as the default library")
    .action(async (name: string) => {
      const { manager, close } = await openLibraryManager();
      try {
        const updated = await manager.setDefault(name);
        logOk(`Default → ${updated.name}`);
      } finally {
        close();
      }
    });

  // ── library rename ───────────────────────────────────────────────────────
  cmd
    .command("rename <old> <new>")
    .description("Rename a library (also moves the directory)")
    .action(async (oldName: string, newName: string) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(oldName);
        const parent = path.dirname(lib.path);
        const newPath = path.join(parent, newName);
        if (fs.existsSync(newPath)) {
          logErr(`Target directory already exists: ${newPath}`);
          process.exit(1);
        }
        fs.renameSync(lib.path, newPath);
        await manager.updateLibrary(lib.id, { name: newName, path: newPath });
        logOk(`Renamed ${oldName} → ${newName}`);
      } finally {
        close();
      }
    });

  // ── library set ──────────────────────────────────────────────────────────
  cmd
    .command("set <name> <field=value>")
    .description("Update a single library field (e.g. ownership=contributor)")
    .action(async (name: string, expr: string) => {
      const [field, ...rest] = expr.split("=");
      const value = rest.join("=");
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const patch: Record<string, unknown> = {};
        if (field === "ownership") patch.ownership = value;
        else if (field === "structure") patch.structure = value;
        else if (field === "isDefault") patch.isDefault = value === "true";
        else if (field.startsWith("backendConfig.")) {
          const key = field.split(".")[1];
          patch.backendConfig = { ...lib.backendConfig, [key]: value };
        } else {
          logErr(`Unsupported field: ${field}`);
          process.exit(1);
        }
        await manager.updateLibrary(name, patch);
        logOk(`Updated ${name}.${field}`);
      } finally {
        close();
      }
    });

  // ── library remove ───────────────────────────────────────────────────────
  cmd
    .command("remove <name>")
    .description("Unregister a library (use --purge to also delete files)")
    .option("--purge", "Also delete the library directory from disk", false)
    .option("-y, --yes", "Skip confirmation", false)
    .action(async (name: string, opts: { purge: boolean; yes: boolean }) => {
      const { manager, close } = await openLibraryManager();
      try {
        if (!opts.yes) {
          const confirmed = await p.confirm({
            message: `Remove library "${name}"${opts.purge ? " AND delete its directory" : ""}?`,
            initialValue: false,
          });
          if (!confirmed || p.isCancel(confirmed)) {
            logInfo("Cancelled.");
            return;
          }
        }
        await manager.removeLibrary(name, { purge: opts.purge });
        logOk(`Removed ${name}.`);
      } finally {
        close();
      }
    });

  // ── library sync ─────────────────────────────────────────────────────────
  cmd
    .command("sync <name>")
    .description("Pull then push (as backend permits)")
    .action(async (name: string) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const drv = manager.driverFor(lib.backend);
        const pull = await drv.pull(lib);
        logInfo(`Pulled: applied=${pull.applied}, conflicts=${pull.conflicts.length}`);
        if (lib.ownership === "owner" && lib.backend !== "local") {
          const push = await drv.push(lib);
          logInfo(`Pushed: ${push.pushed}`);
        }
        await manager.updateLibrary(name, { lastSyncAt: new Date() });
        logOk(`Synced ${name}.`);
      } finally {
        close();
      }
    });

  // ── library index ────────────────────────────────────────────────────────
  // Re-scan a library's on-disk assets into asset_definitions so `add` /
  // `asset install` / `search --local` resolve them. Distinct from `sync`
  // (git pull/push): this indexes local bytes, no remote round-trip.
  cmd
    .command("index <name>")
    .description("(Re)index a library's assets into the local index")
    .action(async (name: string) => {
      const { manager, library, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        // Sync the manifest inventory with disk first, so hand-added assets are
        // both indexed AND publishable (the manifest is the publish allowlist).
        syncManifestWithDisk(lib.path, lib.name);
        const res = await indexLibrary(lib, library);
        for (const e of res.errors) logWarn(e);
        logOk(`Indexed ${res.assetsUpdated} asset(s) from ${name}.`);
      } finally {
        close();
      }
    });

  // ── library pull ─────────────────────────────────────────────────────────
  cmd
    .command("pull <name>")
    .description("Pull from remote")
    .action(async (name: string) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const res = await manager.driverFor(lib.backend).pull(lib);
        if (res.conflicts.length > 0) {
          logWarn(`Conflicts: ${res.conflicts.map((c) => c.path).join(", ")}`);
        } else {
          logOk(`Pulled ${res.applied} change(s).`);
        }
      } finally {
        close();
      }
    });

  // ── library push ─────────────────────────────────────────────────────────
  cmd
    .command("push <name>")
    .description("Push local commits (owner only)")
    .option("--force", "Force-with-lease push", false)
    .action(async (name: string, opts: { force: boolean }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const res = await manager.driverFor(lib.backend).push(lib, { force: opts.force });
        logOk(`Pushed (${res.pushed}).`);
      } catch (err) {
        logErr(err instanceof Error ? err.message : String(err));
        process.exit(1);
      } finally {
        close();
      }
    });

  // ── library propose ──────────────────────────────────────────────────────
  cmd
    .command("propose <name>")
    .description("Open a pull request on the upstream repo (contributor only)")
    .option("--title <title>", "PR title")
    .option("--body <body>", "PR body")
    .option("--branch <name>", "Branch name (default: skaile/<timestamp>)")
    .action(async (name: string, opts: { title?: string; body?: string; branch?: string }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const res = await manager.driverFor(lib.backend).propose(lib, opts);
        logOk(`PR opened: ${res.prUrl}`);
      } catch (err) {
        logErr(err instanceof Error ? err.message : String(err));
        process.exit(1);
      } finally {
        close();
      }
    });

  // ── library commit ───────────────────────────────────────────────────────
  cmd
    .command("commit <name>")
    .description("Git-commit local changes inside the library (git only)")
    .option("-m, --message <msg>", "Commit message", "skaile: update assets")
    .action(async (name: string, opts: { message: string }) => {
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        if (lib.backend !== "git") {
          logErr("`library commit` only applies to git-backed libraries.");
          process.exit(1);
        }
        const { spawnSync } = await import("node:child_process");
        spawnSync("git", ["add", "-A"], { cwd: lib.path, stdio: "inherit" });
        const r = spawnSync("git", ["commit", "-m", opts.message], {
          cwd: lib.path,
          stdio: "inherit",
        });
        if (r.status !== 0) {
          logWarn("Nothing committed (working tree clean or commit aborted).");
        } else {
          logOk(`Committed in ${name}.`);
        }
      } finally {
        close();
      }
    });

  // ── library git ──────────────────────────────────────────────────────────
  cmd
    .command("git <name>")
    .description("Pass-through: run arbitrary git commands inside the library")
    .allowUnknownOption()
    .action(async (name: string, _: unknown, command: Command) => {
      const args = command.args.slice(1);
      const { manager, close } = await openLibraryManager();
      try {
        const lib = await manager.requireLibrary(name);
        const { spawnSync } = await import("node:child_process");
        const r = spawnSync("git", args, { cwd: lib.path, stdio: "inherit" });
        process.exit(r.status ?? 0);
      } finally {
        close();
      }
    });

  // ── library publish / register / yank ────────────────────────────────────
  cmd
    .command("publish <lib>")
    .description("Push + register the library's manifest atomically (needs git backend)")
    .option("--asset <ref>", "Publish only the single asset matching this ref/name")
    .option("--create-repo", "Provision + link a public github repo if the library has no remote")
    .option("--ref <gitref>", "Git ref to ingest (default: the library branch or main)")
    .option("--dry-run", "Print the plan; push nothing and call no store endpoint")
    .action(
      async (
        lib: string,
        opts: { asset?: string; createRepo?: boolean; ref?: string; dryRun?: boolean },
      ) => {
        const { runLibraryPublish } = await import("./library-publish.ts");
        await withStoreErrorHandling(() => runLibraryPublish(lib, opts));
      },
    );

  cmd
    .command("register <lib>")
    .description("Register the library's manifest without pushing (bytes already on github)")
    .option("--asset <ref>", "Publish only the single asset matching this ref/name")
    .option("--ref <gitref>", "Git ref to ingest (default: the library branch or main)")
    .option("--dry-run", "Print the plan; call no store endpoint")
    .action(async (lib: string, opts: { asset?: string; ref?: string; dryRun?: boolean }) => {
      const { runLibraryRegister } = await import("./library-publish.ts");
      await withStoreErrorHandling(() => runLibraryRegister(lib, opts));
    });

  cmd
    .command("yank <lib>")
    .description("Retract the library's published assets from the store")
    .option("--asset <ref>", "Yank only the single asset matching this ref/name")
    .action(async (lib: string, opts: { asset?: string }) => {
      const { runLibraryYank } = await import("./library-publish.ts");
      await withStoreErrorHandling(() => runLibraryYank(lib, opts));
    });

  return cmd;
}

/**
 * Run a store-publishing action, surfacing the store's error message cleanly
 * (e.g. the reserved-namespace hint) via `logErr` instead of letting it crash
 * with a raw stack trace. Clean `process.exit(1)` paths inside `fn` are
 * unaffected (they exit before this catch).
 */
async function withStoreErrorHandling(fn: () => Promise<void>): Promise<void> {
  try {
    await fn();
  } catch (err) {
    logErr(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}

// ---------------------------------------------------------------------------
// Publisher stamping (library create)
// ---------------------------------------------------------------------------

/**
 * Stamp `metadata.publisher` into a scaffolded asset's markdown marker so the
 * asset carries its publisher at the source. Only markdown markers (SKILL.md /
 * AGENT.md / CONTRACT.md / `*.prompt.md`) carry frontmatter — a YAML marker
 * (flow / bundle) is left untouched. Best-effort: a missing/odd marker is a
 * no-op (the manifest publisher is the authoritative index key regardless).
 */
function stampAssetPublisher(assetDir: string, publisher: string): void {
  const markers = ["SKILL.md", "AGENT.md", "CONTRACT.md"];
  let markerFile: string | undefined;
  for (const m of markers) {
    if (fs.existsSync(path.join(assetDir, m))) {
      markerFile = path.join(assetDir, m);
      break;
    }
  }
  if (!markerFile) {
    // Only a `.prompt.md` marker carries asset frontmatter — never a bare `.md`
    // catch-all, which would stamp (and corrupt) a template's README/CHANGELOG.
    const promptMd = fs.readdirSync(assetDir).find((f) => f.endsWith(".prompt.md"));
    if (promptMd) markerFile = path.join(assetDir, promptMd);
  }
  if (!markerFile) return;

  try {
    const { data, body } = parseFrontmatter(fs.readFileSync(markerFile, "utf8"));
    const meta =
      data.metadata && typeof data.metadata === "object" && !Array.isArray(data.metadata)
        ? { ...(data.metadata as Record<string, unknown>) }
        : {};
    if (meta.publisher != null) return; // Respect an author-set publisher.
    meta.publisher = publisher;
    data.metadata = meta;
    const fm = stringifyYaml(data).trimEnd();
    fs.writeFileSync(markerFile, `---\n${fm}\n---\n\n${body}\n`, "utf8");
  } catch {
    // Non-fatal — the manifest publisher still drives indexing.
  }
}

/**
 * Ensure the library's `skaile.manifest.yaml` declares `publisher: <publisher>`.
 * This is the key discovery reads to key assets under the right publisher; the
 * frontmatter stamp above is documentary. Preserves an existing `publisher`
 * (never clobbers a library that already declared one) and the asset inventory.
 */
function ensureManifestPublisher(libraryPath: string, publisher: string): void {
  const manifest = loadSkPublishManifest(libraryPath) ?? { assets: [] };
  if (manifest.publisher) return;
  saveSkPublishManifest(libraryPath, { ...manifest, publisher });
}

/**
 * Reconcile the manifest `assets[]` inventory with what is actually on disk:
 * scan the library (layout-tolerant) and upsert any asset missing from the
 * inventory. `library publish` filters store-discovered candidates by this
 * inventory, so a hand-added asset (created outside `library create`) would
 * otherwise be indexable but not publishable. Idempotent — existing entries are
 * left as-is (upsert keys by kind+name). Never removes entries (curation-safe).
 */
function syncManifestWithDisk(libraryPath: string, libraryName: string): void {
  const publisher = loadSkPublishManifest(libraryPath)?.publisher ?? slugifyAssetName(libraryName);
  for (const e of scanDirectory(libraryPath, publisher)) {
    const root = path.relative(libraryPath, path.dirname(e.source)) || e.name;
    upsertManifestAsset(libraryPath, { kind: e.kind, name: e.name, root, version: e.version });
  }
}

// ---------------------------------------------------------------------------
// Status helpers (formerly `lib-status`)
// ---------------------------------------------------------------------------

async function printPerLibraryStatus(name: string, asJson: boolean): Promise<void> {
  const { manager, close } = await openLibraryManager();
  try {
    const lib = await manager.requireLibrary(name);
    const status = await manager.driverFor(lib.backend).status(lib);
    if (asJson) return console.log(JSON.stringify({ library: lib.name, status }, null, 2));
    console.log(`\n  ${S.heading(lib.name)}`);
    console.log(`    reachable: ${status.reachable ? S.ok("yes") : S.err("no")}`);
    if (status.remoteChanges) {
      console.log(`    behind/ahead: ${status.remoteChanges.behind}/${status.remoteChanges.ahead}`);
    }
    const lc = status.localChanges;
    if (lc.added.length + lc.modified.length + lc.deleted.length > 0) {
      console.log(
        `    local changes: +${lc.added.length} ~${lc.modified.length} -${lc.deleted.length}`,
      );
    }
    for (const note of status.notes) console.log(`    ${pc.dim("note:")} ${note}`);
    console.log();
  } finally {
    close();
  }
}

async function printOverallStatus(projectDir: string, asJson: boolean): Promise<void> {
  const library = await openLibrary();
  try {
    const libs = await library.listSources();
    const defs = await library.listAssetDefs();
    const instances = await library.listInstances();

    let secretsStrategy = "unknown";
    try {
      const { LocalSecretsProvider } = await import("@skaile/workspaces/secrets");
      const provider = new LocalSecretsProvider();
      secretsStrategy = provider.getKeyStrategy();
    } catch {
      secretsStrategy = "unavailable";
    }

    const resolvedProjectDir = path.resolve(projectDir);
    const hasWorkspaceConfig = existsSync(path.join(resolvedProjectDir, "skaile.yaml"));
    const hasLockFile = existsSync(path.join(resolvedProjectDir, "skaile.lock.yaml"));

    let assignments: { id: string; workspaceId: string; instanceId: string }[] = [];
    if (hasWorkspaceConfig) {
      try {
        assignments = await library.listAssignments(resolvedProjectDir);
      } catch {
        // empty workspace
      }
    }

    const registry = createFullRegistry();
    const registeredKinds = registry.getAllKinds();

    if (asJson) {
      console.log(
        JSON.stringify(
          {
            libraries: libs.length,
            assetDefinitions: defs.length,
            instances: instances.length,
            assignments: assignments.length,
            secretsStrategy,
            registeredKinds,
            workspace: {
              dir: resolvedProjectDir,
              hasConfig: hasWorkspaceConfig,
              hasLock: hasLockFile,
            },
          },
          null,
          2,
        ),
      );
      return;
    }

    console.log();
    console.log(S.heading("  Library Status"));
    console.log(`  ${S.rule(50)}`);
    console.log(
      `  ${S.label("Libraries")}        ${libs.length === 0 ? S.dim("none") : pc.green(String(libs.length))}`,
    );
    for (const l of libs.slice(0, 3)) {
      const synced = l.lastSyncAt ? `synced ${formatRelativeTime(l.lastSyncAt)}` : "never synced";
      const defaultMark = l.isDefault ? pc.green(" *") : "";
      console.log(
        `    ${S.dim(l.id.slice(0, 8))} ${l.name} ${S.dim(`(${l.backend})`)} ${S.dim(l.ownership)} ${S.dim(synced)}${defaultMark}`,
      );
    }
    if (libs.length > 3) {
      console.log(`    ${S.dim(`...and ${libs.length - 3} more`)}`);
    }

    const byKind = new Map<string, number>();
    for (const d of defs) byKind.set(d.kind, (byKind.get(d.kind) ?? 0) + 1);
    console.log(
      `  ${S.label("Cached assets")}    ${defs.length === 0 ? S.dim("none") : pc.green(String(defs.length))}`,
    );
    if (byKind.size > 0) {
      const parts = [...byKind.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${v} ${k}${v > 1 ? "s" : ""}`);
      console.log(`    ${S.dim(parts.join(", "))}`);
    }

    console.log(`  ${S.label("Registered kinds")}  ${pc.green(String(registeredKinds.length))}`);
    console.log(`    ${S.dim(registeredKinds.join(", "))}`);

    console.log(
      `  ${S.label("Instances")}        ${instances.length === 0 ? S.dim("none") : pc.green(String(instances.length))}`,
    );
    console.log(
      `  ${S.label("Assignments")}      ${assignments.length === 0 ? S.dim("none (this workspace)") : pc.green(String(assignments.length))}`,
    );

    const strategyColor = secretsStrategy === "unavailable" ? S.dim : pc.green;
    console.log(`  ${S.label("Secrets strategy")}  ${strategyColor(secretsStrategy)}`);

    console.log(`  ${S.rule(50)}`);
    console.log(`  ${S.label("Workspace")}        ${resolvedProjectDir}`);
    console.log(
      `  ${S.label("skaile.yaml")}      ${hasWorkspaceConfig ? pc.green("present") : S.dim("not found")}`,
    );
    console.log(
      `  ${S.label("skaile.lock.yaml")} ${hasLockFile ? pc.green("present") : S.dim("not found")}`,
    );
    console.log();

    if (libs.length === 0) {
      logInfo("Get started: `skaile library init personal` or `skaile source add <git-url>`");
    } else if (defs.length === 0) {
      logInfo("Run `skaile library sync <name>` to discover assets.");
    } else if (instances.length === 0) {
      logInfo("Run `skaile asset install <ref>` to install an asset into a workspace.");
    }
  } finally {
    library.close();
  }
}
